import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Phone, Bike, Clock, Star, X, FileDown, Train, Film, CalendarDays, Navigation, MapPin, XCircle, Loader2, AlertTriangle } from "lucide-react";
import { StatusBadge } from "@/components/common";
import { LiveTrackMap } from "@/components/customer/LiveTrackMap";
import { api, inr, apiError, API, TOKEN_KEY } from "@/lib/api";

const STEPS = [
  ["placed", "Order Placed", "We've received your order."],
  ["confirmed", "Restaurant Confirmed", "The restaurant accepted your order."],
  ["preparing", "Preparing", "Your food is being freshly prepared."],
  ["ready", "Order Ready", "Your order is ready for pickup."],
  ["picked_up", "Picked Up", "Your rider has collected your order."],
  ["on_the_way", "Out for Delivery", "Your order is on the way to you."],
  ["delivered", "Delivered", "Enjoy your meal!"],
];

export default function OrderTracking() {
  const { id } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [track, setTrack] = useState(null);
  const [route, setRoute] = useState(null);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [rating, setRating] = useState({ restaurant_rating: 5, food_rating: 5, delivery_rating: 5, text: "" });
  const [rated, setRated] = useState(false);

  const fetchOrder = () => api.get(`/orders/${id}`).then((r) => {
    setOrder(r.data);
    if (!["delivered", "cancelled"].includes(r.data.status)) {
      api.get(`/orders/${id}/track`).then((t) => setTrack(t.data)).catch(() => {});
    }
  }).catch(() => {});
  useEffect(() => {
    fetchOrder();
    const t = setInterval(() => {
      setOrder((o) => {
        if (o && ["delivered", "cancelled"].includes(o.status)) { clearInterval(t); return o; }
        fetchOrder();
        return o;
      });
    }, 4000);
    return () => clearInterval(t);
  }, [id]);

  const doCancel = async () => {
    setCancelling(true);
    try {
      await api.post(`/orders/${id}/cancel`, {});
      toast.success("Your order has been cancelled");
      setShowCancel(false);
      await fetchOrder();
    } catch (e) { toast.error(apiError(e)); }
    finally { setCancelling(false); }
  };

  const nearFiredRef = useRef(false);
  useEffect(() => {
    const inDel = order && ["picked_up", "on_the_way"].includes(order.status);
    if (inDel && track?.rider?.lat && route && route.etaMin <= 3 && !nearFiredRef.current) {
      nearFiredRef.current = true;
      toast.success("Your rider is almost here — please be ready! 🛵", { duration: 6000 });
    }
  }, [order?.status, track?.rider?.lat, route?.etaMin]);

  if (!order) return <div className="min-h-screen bg-surface max-w-[520px] mx-auto p-4"><div className="skel h-40 rounded-2xl" /></div>;

  const doneIdx = STEPS.findIndex((s) => s[0] === order.status);
  const isDelivered = order.status === "delivered";
  const isCancelled = order.status === "cancelled";
  const canCancel = ["placed", "confirmed"].includes(order.status);
  const inDelivery = ["picked_up", "on_the_way"].includes(order.status);
  const phase = inDelivery ? "delivery" : "prep";
  const hasMap = track && track.customer && track.customer.lat && (track.restaurant?.lat || track.rider?.lat);
  const riderMissing = inDelivery && !track?.rider?.lat;
  const arrivingSoon = inDelivery && track?.rider?.lat && route && route.etaMin <= 3;
  const histAt = (st) => { const h = (order.status_history || []).find((x) => x.status === st); return h ? new Date(h.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""; };

  const submitRating = async () => {
    try { const { data } = await api.post(`/orders/${id}/rate`, rating); setRated(true); toast.success(`Thanks! You earned ${data.points_earned} points`); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="min-h-screen bg-surface max-w-[520px] mx-auto pb-10">
      <div className="sticky top-0 z-40 bg-white border-b border-border px-3 py-3 flex items-center gap-2">
        <button onClick={() => nav("/orders")} className="w-9 h-9 flex items-center justify-center tap"><ArrowLeft size={22} /></button>
        <div><h1 className="font-display font-bold text-base">Order {order.code}</h1><span className="text-xs text-ink-600">{order.restaurant_name}</span></div>
        <div className="ml-auto"><StatusBadge status={order.status} /></div>
      </div>

      <div className="px-4 py-4 space-y-3">
        {order.source === "meal_subscription" && (
          <div className="bg-white rounded-2xl border-2 border-coral/40 p-4" data-testid="meal-order-badge">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-coral-light flex items-center justify-center"><CalendarDays size={18} className="text-coral" /></div>
              <div className="min-w-0">
                <div className="font-display font-bold text-sm text-ink flex items-center gap-2 capitalize">Meal Subscription <span className="text-[10px] font-bold uppercase bg-coral text-white px-1.5 py-0.5 rounded">{order.meal_type}</span></div>
                <div className="text-xs text-ink-600 truncate">Auto-delivered as part of your {order.meal_type} plan{order.meal_date ? ` · ${order.meal_date}` : ""}</div>
              </div>
            </div>
          </div>
        )}
        {order.cinema_ctx && (
          <div className="bg-white rounded-2xl border-2 border-fuchsia-400/50 p-4" data-testid="cinema-order-badge">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-fuchsia-100 flex items-center justify-center"><Film size={18} className="text-fuchsia-600" /></div>
              <div className="min-w-0">
                <div className="font-display font-bold text-sm text-ink flex items-center gap-2">Cinema · Seat delivery <span className="text-[10px] font-bold uppercase bg-fuchsia-600 text-white px-1.5 py-0.5 rounded">Movie</span></div>
                <div className="text-xs text-ink-600 truncate">{order.cinema_ctx.movie} · {order.cinema_ctx.cinema}</div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
              {order.cinema_ctx.screen && <div className="bg-surface rounded-lg px-2 py-1.5"><div className="text-ink-600">Screen</div><div className="font-bold text-ink truncate">{order.cinema_ctx.screen}</div></div>}
              {order.cinema_ctx.showtime && <div className="bg-surface rounded-lg px-2 py-1.5"><div className="text-ink-600">Show</div><div className="font-bold text-ink">{order.cinema_ctx.showtime}</div></div>}
              {order.cinema_ctx.seat && <div className="bg-surface rounded-lg px-2 py-1.5"><div className="text-ink-600">Seat</div><div className="font-bold text-ink">{order.cinema_ctx.seat}</div></div>}
            </div>
          </div>
        )}
        {order.train_ctx && (
          <div className="bg-white rounded-2xl border-2 border-coral/40 p-4" data-testid="train-order-badge">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-coral-light flex items-center justify-center"><Train size={18} className="text-coral" /></div>
              <div className="min-w-0">
                <div className="font-display font-bold text-sm text-ink flex items-center gap-2">Food on Train <span className="text-[10px] font-bold uppercase bg-coral text-white px-1.5 py-0.5 rounded">Seat delivery</span></div>
                <div className="text-xs text-ink-600 truncate">{order.train_ctx.train}{order.train_ctx.pnr ? ` · PNR ${order.train_ctx.pnr}` : ""}</div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
              {order.train_ctx.coach && <div className="bg-surface rounded-lg px-2 py-1.5"><div className="text-ink-600">Coach</div><div className="font-bold text-ink">{order.train_ctx.coach}</div></div>}
              {order.train_ctx.seat && <div className="bg-surface rounded-lg px-2 py-1.5"><div className="text-ink-600">Seat</div><div className="font-bold text-ink">{order.train_ctx.seat}</div></div>}
              {order.train_ctx.station && <div className="bg-surface rounded-lg px-2 py-1.5"><div className="text-ink-600">Station</div><div className="font-bold text-ink truncate">{order.train_ctx.station}</div></div>}
            </div>
            {order.train_ctx.eta && <div className="text-[11px] text-ink-600 mt-2">Delivering at station · ETA {order.train_ctx.eta}</div>}
          </div>
        )}
        {isCancelled && (
          <div className="bg-white rounded-2xl border-2 border-red-200 p-4" data-testid="cancelled-banner">
            <div className="flex items-center gap-2 text-red-600"><XCircle size={20} /><span className="font-bold">Order cancelled</span></div>
            <p className="text-sm text-ink-600 mt-1">
              {order.cancelled_at ? `Cancelled on ${new Date(order.cancelled_at).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}. ` : ""}
              {order.cancel_reason || "This order was cancelled."}
            </p>
            {order.refund ? (
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm" data-testid="refund-status">
                <div className="font-semibold text-amber-800">Refund {order.refund.status === "completed" ? "completed" : "initiated"}</div>
                <div className="text-xs text-amber-700 mt-0.5">{inr(order.refund.amount)} to your original payment method{order.refund.status === "completed" ? " — processed." : " · 5–7 business days."}</div>
              </div>
            ) : (
              <div className="mt-3 bg-surface rounded-xl p-3 text-xs text-ink-600">This was a Cash on Delivery order — no payment was taken, so no refund is required.</div>
            )}
          </div>
        )}
        {!isCancelled && (
          <div className="bg-gradient-to-r from-coral to-coral-dark text-white rounded-2xl p-4">
            <div className="flex items-center gap-2"><CheckCircle2 size={20} /><span className="font-bold">{isDelivered ? "Delivered!" : "Order confirmed"}</span></div>
            <p className="text-sm text-white/90 mt-1">
              {isDelivered ? "Hope you enjoyed your meal." : `Estimated delivery in ${order.prep_time + order.restaurant_name.length % 10 + 15} min`}
            </p>
            {!isDelivered && (
              <div className="mt-3 bg-white/15 rounded-xl p-3 text-xs grid grid-cols-3 gap-2">
                <div><div className="opacity-80">Preparation</div><div className="font-bold">{order.prep_time} min</div></div>
                <div><div className="opacity-80">Pickup</div><div className="font-bold">5 min</div></div>
                <div><div className="opacity-80">Travel</div><div className="font-bold">12 min</div></div>
              </div>
            )}
          </div>
        )}

        {arrivingSoon && (
          <div className="bg-veg text-white rounded-2xl p-3.5 flex items-center gap-2.5 shadow-lg" data-testid="arriving-banner">
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center animate-pulse"><Bike size={18} /></div>
            <div className="min-w-0"><div className="font-bold text-sm">Your rider is almost here!</div><div className="text-xs text-white/85">Arriving in ~{route.etaMin} min · please be ready to receive your order</div></div>
          </div>
        )}
        {!isCancelled && !isDelivered && hasMap && (
          <div className="bg-white rounded-2xl border border-border overflow-hidden" data-testid="live-tracking-card">
            <div className="relative">
              <LiveTrackMap restaurant={track.restaurant} customer={track.customer} rider={track.rider} phase={phase} onRoute={setRoute} />
              <div className="absolute top-2 left-2 right-2 flex items-center justify-between gap-2 pointer-events-none">
                <span className="bg-white/95 backdrop-blur px-3 py-1.5 rounded-full text-xs font-bold text-ink shadow flex items-center gap-1.5">
                  <Navigation size={12} className="text-coral" /> {STEPS[doneIdx] ? STEPS[doneIdx][1] : "Tracking"}
                </span>
                {route && (
                  <span className="bg-coral text-white px-3 py-1.5 rounded-full text-xs font-bold shadow flex items-center gap-1.5" data-testid="live-eta">
                    <Clock size={12} /> {route.etaMin} min · {route.distanceKm.toFixed(1)} km
                  </span>
                )}
              </div>
              {riderMissing && (
                <div className="absolute bottom-2 left-2 right-2 bg-white/95 backdrop-blur px-3 py-2 rounded-xl text-xs text-ink-600 shadow flex items-center gap-2" data-testid="rider-location-updating">
                  <Loader2 size={13} className="text-coral animate-spin" /> Locating your rider… live location updating.
                </div>
              )}
            </div>
            <div className="px-4 py-2.5 border-t border-border flex items-center justify-between text-[11px] text-ink-600">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-ink inline-block" /> {track.restaurant?.name || "Restaurant"}</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-veg inline-block" /> {track.customer?.label || "You"}</span>
            </div>
          </div>
        )}

        {order.delivery_partner && !isCancelled && (
          <div className="bg-white rounded-2xl border border-border p-4 flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-coral-light flex items-center justify-center"><Bike size={22} className="text-coral" /></div>
            <div className="flex-1 min-w-0"><div className="font-bold text-sm">{order.delivery_partner.name}</div><div className="text-xs text-ink-600 truncate">{order.delivery_partner.vehicle} · <Star size={10} className="inline fill-gold text-gold" /> {order.delivery_partner.rating}</div></div>
            <a href={`tel:${order.delivery_partner.phone}`} className="w-9 h-9 rounded-full bg-veg text-white flex items-center justify-center tap"><Phone size={16} /></a>
          </div>
        )}
        {order.delivery_otp && !isDelivered && !isCancelled && order.delivery_partner && (
          <div className="bg-white rounded-2xl border border-border p-4 text-center">
            <div className="text-xs text-ink-600">Share this OTP with your rider at delivery</div>
            <div className="text-3xl font-display font-extrabold tracking-[0.3em] text-coral mt-1" data-testid="delivery-otp">{order.delivery_otp}</div>
          </div>
        )}

        {!isCancelled && (
          <div className="bg-white rounded-2xl border border-border p-4">
            <h3 className="font-bold text-ink mb-3 flex items-center gap-2"><Clock size={16} className="text-coral" /> Order status</h3>
            <div className="relative pl-6">
              {STEPS.map((s, i) => {
                const done = i <= doneIdx;
                const active = i === doneIdx;
                return (
                  <div key={s[0]} className="relative pb-5 last:pb-0">
                    {i < STEPS.length - 1 && <span className={`absolute left-[-15px] top-4 w-0.5 h-full ${i < doneIdx ? "bg-coral" : "bg-border"}`} />}
                    <span className={`absolute left-[-20px] top-0.5 w-3.5 h-3.5 rounded-full border-2 ${done ? "bg-coral border-coral" : "bg-white border-border"} ${active && !isDelivered ? "ring-4 ring-coral/25 animate-pulse" : ""}`} />
                    <div className="flex items-center justify-between">
                      <div className={`text-sm font-semibold ${done ? "text-ink" : "text-ink-600"}`}>{s[1]}</div>
                      {done && <span className="text-[11px] text-ink-600" data-testid={`ts-${s[0]}`}>{histAt(s[0])}</span>}
                    </div>
                    {active && <div className="text-xs text-ink-600 mt-0.5">{s[2]}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {canCancel && (
          <button
            onClick={() => setShowCancel(true)}
            className="w-full bg-white border-2 border-red-200 text-red-600 rounded-2xl py-3.5 font-bold text-sm flex items-center justify-center gap-2 tap hover:bg-red-50 transition-colors"
            data-testid="cancel-order-btn"
          >
            <XCircle size={17} /> Cancel Order
          </button>
        )}

        <div className="bg-white rounded-2xl border border-border p-4">
          <h3 className="font-bold text-ink mb-2">{order.items.length} items</h3>
          {order.items.map((it, i) => (
            <div key={i} className="flex justify-between text-sm py-1"><span className="text-ink-600">{it.qty} × {it.name}</span><span>{inr(it.line_total || it.unit_price * it.qty)}</span></div>
          ))}
          <div className="flex justify-between border-t border-border mt-2 pt-2 font-bold"><span>Total ({order.payment_method?.toUpperCase()})</span><span>{inr(order.total)}</span></div>
        </div>

        {isDelivered && (
          <button
            onClick={() => window.open(`${API}/orders/${order.id}/invoice?auth=${localStorage.getItem(TOKEN_KEY)}`, "_blank")}
            className="w-full bg-white border border-border rounded-2xl py-3 font-bold text-sm text-coral flex items-center justify-center gap-2 tap"
            data-testid="download-invoice-btn"
          >
            <FileDown size={16} /> Download Invoice (PDF)
          </button>
        )}

        {isDelivered && !order.rating && !rated && (
          <div className="bg-white rounded-2xl border border-border p-4" data-testid="rating-box">
            <h3 className="font-bold text-ink mb-2">Rate your order</h3>
            {[["restaurant_rating", "Restaurant"], ["food_rating", "Food"], ["delivery_rating", "Delivery"]].map(([k, l]) => (
              <div key={k} className="flex items-center justify-between py-1"><span className="text-sm text-ink-600">{l}</span>
                <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => setRating((r) => ({ ...r, [k]: n }))} data-testid={`rate-${k}-${n}`}><Star size={22} className={n <= rating[k] ? "fill-gold text-gold" : "text-border"} /></button>)}</div>
              </div>
            ))}
            <input value={rating.text} onChange={(e) => setRating((r) => ({ ...r, text: e.target.value }))} placeholder="Write a review (optional)" className="w-full bg-surface rounded-xl px-3 py-2.5 text-sm mt-2 outline-none" />
            <button onClick={submitRating} className="w-full bg-coral text-white font-bold py-3 rounded-xl mt-3 tap" data-testid="submit-rating">Submit Rating</button>
          </div>
        )}
        {(order.rating || rated) && isDelivered && <div className="bg-emerald-50 text-veg rounded-2xl p-4 text-center font-semibold text-sm">Thanks for rating this order!</div>}

        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => nav(`/restaurant/${order.restaurant_id}`)} className="bg-white border border-border rounded-xl py-3 font-bold text-sm text-coral tap" data-testid="reorder-btn">Reorder</button>
          <button onClick={() => nav("/support")} className="bg-white border border-border rounded-xl py-3 font-bold text-sm text-ink tap">Get Help</button>
        </div>
      </div>

      {showCancel && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-4" data-testid="cancel-modal">
          <div className="bg-white rounded-3xl w-full max-w-[400px] p-5 animate-[slideUp_.2s_ease]">
            <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mx-auto"><AlertTriangle size={24} className="text-red-500" /></div>
            <h3 className="font-display font-bold text-lg text-ink text-center mt-3">Cancel this order?</h3>
            <p className="text-sm text-ink-600 text-center mt-1">Are you sure you want to cancel your order?{order.payment_method !== "cod" && order.payment_status === "paid" ? " Your payment will be refunded to the original method." : ""}</p>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowCancel(false)} disabled={cancelling} className="flex-1 bg-surface border border-border rounded-xl py-3 font-bold text-sm text-ink tap" data-testid="keep-order-btn">Keep Order</button>
              <button onClick={doCancel} disabled={cancelling} className="flex-1 bg-red-500 text-white rounded-xl py-3 font-bold text-sm flex items-center justify-center gap-2 tap disabled:opacity-60" data-testid="confirm-cancel-btn">
                {cancelling ? <Loader2 size={16} className="animate-spin" /> : <XCircle size={16} />} Cancel Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
